/**
 * ColumnSortDialog - Modal para configurar coluna do Kanban
 *
 * Permite ao usuário:
 * - Renomear a fase (nome da coluna)
 * - Alterar a cor da fase exibida no Kanban
 * - Definir probabilidade (%) e se a fase é final (ganho/perda) — espelha o editor
 *   de fases do workspace (PipelinePhaseManager)
 * - Selecionar campo de ordenação: Data Criação Negócio | Data Atualização Fase
 * - Selecionar direção: Ascendente | Descendente
 *
 * Preferências de ordenação são salvas em localStorage por phaseId.
 * Nome, cor, probabilidade e flags finais são atualizados no banco (crm_pipeline_phases).
 *
 * @version 3.0.0
 * RELEASE: 2026-09-03 v3.0.0 - Layout premium (mesmo padrão do modal de anotações do
 *          chat: cabeçalho com gradiente + ícone, corpo em FormSectionCard com rolagem
 *          e rodapé fixo). Novos campos editáveis: probabilidade (%) e flags de fase
 *          final (ganho/perda, mutuamente exclusivas), carregados da fase ao abrir.
 * RELEASE: 2026-09-03 v2.1.0 - Adicionada edição da cor da fase diretamente no modal
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { Columns3, TrendingUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

/** Logger para depuração */
const logger = createLogger('ColumnSortDialog');

/** Campos disponíveis para ordenação */
export type ColumnSortField = 'created_at' | 'phase_updated_at';

/** Direções de ordenação */
export type ColumnSortDirection = 'asc' | 'desc';

/** Configuração de ordenação de uma coluna */
export interface ColumnSortConfig {
  sortField: ColumnSortField;
  sortDirection: ColumnSortDirection;
}

/** Chave de localStorage para preferências de ordenação */
const SORT_STORAGE_KEY = 'kanban-column-sort';

/**
 * Recupera a configuração de ordenação salva para uma fase específica
 * @param phaseId - ID da fase
 * @returns Configuração salva ou valores padrão
 */
export function getColumnSortConfig(phaseId: string): ColumnSortConfig {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed[phaseId]) {
        return parsed[phaseId] as ColumnSortConfig;
      }
    }
  } catch {
    // Ignora erros de parse
  }
  // Padrão: ordenar por data de criação, decrescente
  return { sortField: 'created_at', sortDirection: 'desc' };
}

/**
 * Salva a configuração de ordenação para uma fase no localStorage
 * @param phaseId - ID da fase
 * @param config - Configuração de ordenação
 */
function saveColumnSortConfig(phaseId: string, config: ColumnSortConfig): void {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[phaseId] = config;
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Ignora erros de escrita
  }
}

/** Cor padrão quando a fase não possui cor definida (mesmo fallback do PipelinePhaseManager) */
const DEFAULT_PHASE_COLOR = '#94A3B8';

/** Paleta de cores pré-definidas para escolha rápida */
const PRESET_COLORS = [
  '#EF4444', // vermelho
  '#F97316', // laranja
  '#F59E0B', // âmbar
  '#22C55E', // verde
  '#14B8A6', // teal
  '#3B82F6', // azul
  '#8B5CF6', // violeta
  '#EC4899', // rosa
  '#64748B', // cinza azulado
];

/** Valores de comportamento carregados da fase (v3.0.0) */
interface PhaseBehavior {
  probability: number;
  isWon: boolean;
  isLost: boolean;
}

// Props do componente
interface ColumnSortDialogProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para controlar abertura/fechamento */
  onOpenChange: (open: boolean) => void;
  /** ID da fase (para persistência no localStorage e update no banco) */
  phaseId: string;
  /** Nome atual da fase */
  phaseName: string;
  /** Cor atual da fase (hex) — exibida no header da coluna do Kanban */
  phaseColor?: string | null;
  /** Configuração atual de ordenação */
  currentConfig: ColumnSortConfig;
  /** Callback ao salvar nova configuração de ordenação */
  onSave: (config: ColumnSortConfig) => void;
}

/**
 * Modal de configuração de coluna do Kanban
 * (identidade: nome/cor · comportamento: probabilidade/ganho/perda · ordenação dos cards)
 */
export function ColumnSortDialog({
  open,
  onOpenChange,
  phaseId,
  phaseName,
  phaseColor,
  currentConfig,
  onSave,
}: ColumnSortDialogProps) {
  const queryClient = useQueryClient();

  // Cor efetiva inicial da fase (fallback quando não definida)
  const initialColor = phaseColor || DEFAULT_PHASE_COLOR;

  // Estados locais — identidade e ordenação
  const [name, setName] = useState(phaseName);
  const [color, setColor] = useState(initialColor);
  const [sortField, setSortField] = useState<ColumnSortField>(currentConfig.sortField);
  const [sortDirection, setSortDirection] = useState<ColumnSortDirection>(currentConfig.sortDirection);
  const [saving, setSaving] = useState(false);

  // v3.0.0: Estados de comportamento da fase (probabilidade e flags finais)
  const [probability, setProbability] = useState<number>(0);
  const [isWon, setIsWon] = useState(false);
  const [isLost, setIsLost] = useState(false);
  // Valores originais carregados do banco (para comparação no update e reset no cancelar)
  const [loadedBehavior, setLoadedBehavior] = useState<PhaseBehavior | null>(null);
  const [loadingBehavior, setLoadingBehavior] = useState(false);

  /**
   * v3.0.0: Ao abrir, carrega probabilidade e flags da fase (a coluna do Kanban
   * não recebe esses campos por props) e reseta os demais estados.
   */
  useEffect(() => {
    if (!open) return;

    // Reset dos campos de identidade/ordenação a cada abertura
    setName(phaseName);
    setColor(initialColor);
    setSortField(currentConfig.sortField);
    setSortDirection(currentConfig.sortDirection);
    setLoadedBehavior(null);

    let cancelled = false;
    (async () => {
      setLoadingBehavior(true);
      try {
        const { data, error } = await supabase
          .from('crm_pipeline_phases')
          .select('probability_percent, is_won, is_lost')
          .eq('id', phaseId)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        const behavior: PhaseBehavior = {
          probability: Number(data?.probability_percent ?? 0),
          isWon: Boolean(data?.is_won),
          isLost: Boolean(data?.is_lost),
        };
        setLoadedBehavior(behavior);
        setProbability(behavior.probability);
        setIsWon(behavior.isWon);
        setIsLost(behavior.isLost);
      } catch (err) {
        logger.error('Erro ao carregar comportamento da fase:', err);
        if (!cancelled) toast.error('Não foi possível carregar as configurações da fase');
      } finally {
        if (!cancelled) setLoadingBehavior(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phaseId]);

  /**
   * Salva ordenação + campos alterados da fase em um único update
   */
  const handleSave = async () => {
    // Validação: nome não pode ficar vazio
    if (!name.trim()) {
      toast.error('O nome da fase não pode ficar vazio');
      return;
    }

    // v3.0.0: validação da probabilidade (0–100)
    const prob = Math.round(probability);
    if (Number.isNaN(prob) || prob < 0 || prob > 100) {
      toast.error('A probabilidade deve estar entre 0 e 100');
      return;
    }

    setSaving(true);
    try {
      // Monta um único update com todos os campos alterados
      const updates: {
        name?: string;
        color?: string;
        probability_percent?: number;
        is_won?: boolean;
        is_lost?: boolean;
      } = {};
      if (name.trim() !== phaseName) {
        updates.name = name.trim();
      }
      if (color !== initialColor) {
        updates.color = color;
      }
      // Comportamento: compara com o que foi carregado do banco
      if (loadedBehavior) {
        if (prob !== loadedBehavior.probability) updates.probability_percent = prob;
        if (isWon !== loadedBehavior.isWon) updates.is_won = isWon;
        if (isLost !== loadedBehavior.isLost) updates.is_lost = isLost;
      }

      // Atualiza a fase no banco se houve qualquer alteração
      if (Object.keys(updates).length > 0) {
        logger.debug('Atualizando fase:', { phaseId, updates });

        const { error } = await supabase
          .from('crm_pipeline_phases')
          .update(updates)
          .eq('id', phaseId);

        if (error) {
          logger.error('Erro ao atualizar fase:', error);
          toast.error('Erro ao atualizar fase');
          setSaving(false);
          return;
        }

        // Invalida queries para refletir imediatamente no Kanban e no workspace
        queryClient.invalidateQueries({ queryKey: ['pipeline-phase-stats'] });
        queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });

        toast.success('Fase atualizada com sucesso');
      }

      // Salva configuração de ordenação (localStorage)
      const config: ColumnSortConfig = { sortField, sortDirection };
      saveColumnSortConfig(phaseId, config);
      onSave(config);
      onOpenChange(false);
    } catch (err) {
      logger.error('Erro ao salvar configuração da coluna:', err);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Cancela e fecha o modal sem salvar (restaura valores originais)
   */
  const handleCancel = () => {
    setName(phaseName);
    setColor(initialColor);
    setSortField(currentConfig.sortField);
    setSortDirection(currentConfig.sortDirection);
    if (loadedBehavior) {
      setProbability(loadedBehavior.probability);
      setIsWon(loadedBehavior.isWon);
      setIsLost(loadedBehavior.isLost);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden max-h-[90dvh] flex flex-col">
        {/* ===== CABEÇALHO PREMIUM (padrão do modal de anotações do chat) ===== */}
        <div className="px-5 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Columns3 className="h-3.5 w-3.5 text-primary" />
              </div>
              Configurar Coluna
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Edite a fase e a ordenação dos cards desta coluna.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO COM ROLAGEM (seções agrupadas) ===== */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-4">
            {/* Seção: Identidade da fase */}
            <FormSectionCard title="Identidade da fase" icon={Columns3}>
              <div className="space-y-3">
                {/* Nome da fase */}
                <div className="space-y-1.5">
                  <Label htmlFor="phase-name" className="text-xs">Nome da fase</Label>
                  <Input
                    id="phase-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nome da fase..."
                    className="h-9"
                  />
                </div>

                {/* Cor da fase — mesmo padrão visual do PipelinePhaseManager */}
                <div className="space-y-1.5">
                  <Label htmlFor="phase-color" className="text-xs">Cor da fase</Label>
                  <div className="flex items-center gap-3">
                    {/* Seletor nativo de cor */}
                    <input
                      id="phase-color"
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="h-9 w-14 cursor-pointer rounded-md border bg-transparent p-1"
                    />
                    {/* Preview do hex selecionado */}
                    <span className="text-xs text-muted-foreground font-mono uppercase">{color}</span>
                  </div>
                  {/* Swatches de escolha rápida */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {PRESET_COLORS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setColor(preset)}
                        aria-label={`Selecionar cor ${preset}`}
                        className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${
                          color.toLowerCase() === preset.toLowerCase()
                            ? 'ring-2 ring-primary ring-offset-1'
                            : ''
                        }`}
                        style={{ backgroundColor: preset }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </FormSectionCard>

            {/* Seção: Comportamento da fase (v3.0.0) */}
            <FormSectionCard title="Comportamento da fase" icon={TrendingUp}>
              {loadingBehavior ? (
                <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando configurações...
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Probabilidade (%) */}
                  <div className="space-y-1.5">
                    <Label htmlFor="phase-probability" className="text-xs">
                      Probabilidade de ganho (%)
                    </Label>
                    <Input
                      id="phase-probability"
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={Number.isNaN(probability) ? '' : probability}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProbability(v === '' ? 0 : Number(v));
                      }}
                      className="h-9 w-28"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Usada na previsão de receita do pipeline (0 a 100).
                    </p>
                  </div>

                  {/* Flags de fase final — mutuamente exclusivas */}
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <Label className="text-xs">Fase de ganho</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Mover para cá marca o negócio como ganho.
                        </p>
                      </div>
                      <Switch
                        checked={isWon}
                        onCheckedChange={(checked) => {
                          setIsWon(checked);
                          // Ganho e perda são mutuamente exclusivos
                          if (checked) setIsLost(false);
                        }}
                        disabled={loadingBehavior}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <Label className="text-xs">Fase de perda</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Mover para cá exige motivo e marca como perdido.
                        </p>
                      </div>
                      <Switch
                        checked={isLost}
                        onCheckedChange={(checked) => {
                          setIsLost(checked);
                          if (checked) setIsWon(false);
                        }}
                        disabled={loadingBehavior}
                      />
                    </div>
                  </div>
                </div>
              )}
            </FormSectionCard>

            {/* Seção: Ordenação dos cards */}
            <FormSectionCard title="Ordenação dos cards" icon={ArrowUpDown}>
              <div className="grid grid-cols-2 gap-3">
                {/* Campo de ordenação */}
                <div className="space-y-1.5">
                  <Label htmlFor="sort-field" className="text-xs">Ordenar por</Label>
                  <Select
                    value={sortField}
                    onValueChange={(value) => setSortField(value as ColumnSortField)}
                  >
                    <SelectTrigger id="sort-field" className="h-9">
                      <SelectValue placeholder="Campo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Data Criação Negócio</SelectItem>
                      <SelectItem value="phase_updated_at">Data Atualização Fase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Direção da ordenação */}
                <div className="space-y-1.5">
                  <Label htmlFor="sort-direction" className="text-xs">Ordem</Label>
                  <Select
                    value={sortDirection}
                    onValueChange={(value) => setSortDirection(value as ColumnSortDirection)}
                  >
                    <SelectTrigger id="sort-direction" className="h-9">
                      <SelectValue placeholder="Ordem" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascendente</SelectItem>
                      <SelectItem value="desc">Descendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </FormSectionCard>
          </div>
        </ScrollArea>

        {/* ===== RODAPÉ FIXO ===== */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/10">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || loadingBehavior}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
